import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Animated,
  Modal,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { showAlert, showConfirm } from '../utils/alert';
import { useAuthStore } from '../stores/authStore';
import { useTeamStore } from '../stores/teamStore';
import { peekTeamName, resolveTeamNames } from '../utils/teamName';
import { useNotificationStore } from '../stores/notificationStore';
import Icon, { ICON_SIZES } from './Icon';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

const DRAWER_WIDTH = 280;

interface DrawerMenuProps {
  visible: boolean;
  onClose: () => void;
}

export default function DrawerMenu({ visible, onClose }: DrawerMenuProps) {
  const router = useRouter();
  const translateX = useRef(new Animated.Value(-DRAWER_WIDTH)).current;
  const overlayOpacity = useRef(new Animated.Value(0)).current;
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const logoutAndForgetDevice = useAuthStore((s) => s.logoutAndForgetDevice);
  const [isAccessible, setIsAccessible] = useState(false);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});
  const teams = useTeamStore((s) => s.teams);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const notificationUnreadCount = useNotificationStore((s) => s.unreadCount);
  const setActiveTeam = useTeamStore((s) => s.setActiveTeam);
  const fetchTeams = useTeamStore((s) => s.fetchTeams);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  // Signature of the fields name resolution depends on, so the effect below
  // re-runs when a team is added/renamed/rotated but not on every render.
  const teamsSignature = teams
    .map((t) => `${t.id}:${t.current_key_version}:${t.name_encrypted ?? ''}`)
    .join('|');

  const getTeamName = (team: (typeof teams)[number]): string =>
    teamNames[team.id] ?? peekTeamName(team);

  const activeTeam = activeTeamId ? teams.find((t) => t.id === activeTeamId) : null;
  const activeTeamName = activeTeamId === null
    ? 'All Teams'
    : activeTeam
      ? getTeamName(activeTeam)
      : 'Select Team';

  useEffect(() => {
    if (visible && teams.length === 0) {
      fetchTeams();
    }
  }, [visible, teams.length, fetchTeams]);

  // Team names are E2EE — decrypt them off the render path.
  useEffect(() => {
    if (teams.length === 0) return;
    let cancelled = false;
    resolveTeamNames(teams)
      .then((names) => {
        if (!cancelled) setTeamNames(names);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsSignature]);

  useEffect(() => {
    if (visible) {
      setIsAccessible(true);
      Animated.parallel([
        Animated.timing(translateX, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(overlayOpacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      setTeamDropdownOpen(false);
      Animated.parallel([
        Animated.timing(translateX, {
          toValue: -DRAWER_WIDTH,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(overlayOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setIsAccessible(false);
      });
    }
  }, [visible, translateX, overlayOpacity]);

  const handleNavigate = (path: string) => {
    onClose();
    router.push(path as any);
  };

  const handleLogout = async () => {
    onClose();
    await logout();
  };

  const handleLogoutForget = () => {
    showConfirm(
      'Sign Out & Forget Device',
      'This will remove your encryption key from this device. You will need your recovery phrase to sign in again. Continue?',
      () => {
        onClose();
        logoutAndForgetDevice();
      },
      { confirmText: 'Sign Out & Forget', destructive: true }
    );
  };

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose} accessibilityViewIsModal={true}>
      {/* Overlay */}
      <TouchableWithoutFeedback onPress={onClose} accessible={true} accessibilityLabel="Close menu" accessibilityRole="button">
        <Animated.View style={[styles.overlay, { opacity: overlayOpacity }]} />
      </TouchableWithoutFeedback>

      {/* Drawer */}
      <Animated.View style={[styles.drawer, { transform: [{ translateX }] }]} accessibilityElementsHidden={!isAccessible} importantForAccessibility={isAccessible ? 'yes' : 'no-hide-descendants'}>
        {/* Header */}
        <View style={styles.header} accessible={false}>
          <Text style={styles.headerTitle} accessible={false}>Cynosure</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton} accessibilityRole="button" accessibilityLabel="Close menu" testID="drawer.close-button">
            <Icon name="close" size={ICON_SIZES.menu} color={colors.textMuted} />
          </TouchableOpacity>
        </View>

        <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
        {/* User profile */}
        <View style={styles.profileSection} accessible={true} accessibilityLabel={`Signed in as ${user?.display_name || user?.email || 'User'}`}>
          <View style={styles.avatar} accessible={false}>
            <Text style={styles.avatarText} accessible={false}>
              {user?.display_name?.[0]?.toUpperCase() ||
                user?.email?.[0]?.toUpperCase() ||
                'U'}
            </Text>
          </View>
          <View style={styles.profileInfo} accessible={false}>
            <Text style={styles.profileName} numberOfLines={1} accessible={false}>
              {user?.display_name || 'User'}
            </Text>
            <Text style={styles.profileEmail} numberOfLines={1} accessible={false}>
              {user?.email}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Team Selector */}
        {teams.length > 0 && (
          <View style={styles.teamDropdownContainer}>
            <TouchableOpacity
              style={styles.teamDropdownTrigger}
              onPress={() => setTeamDropdownOpen(!teamDropdownOpen)}
              accessibilityRole="button"
              accessibilityLabel={`Active team: ${activeTeamName}. Tap to change`}
              testID="drawer.team-dropdown"
            >
              <Text style={styles.teamDropdownText} accessible={false}>{activeTeamName}</Text>
              <Icon name={teamDropdownOpen ? 'chevron-up' : 'chevron-down'} size={ICON_SIZES.chevron} color={colors.textMuted} />
            </TouchableOpacity>
            {teamDropdownOpen && (
              <View style={styles.teamDropdownList}>
                <TouchableOpacity
                  style={[styles.teamDropdownItem, activeTeamId === null && { backgroundColor: colors.primaryLight }]}
                  onPress={() => {
                    setActiveTeam(null);
                    setTeamDropdownOpen(false);
                  }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: activeTeamId === null }}
                  accessibilityLabel={`All Teams${activeTeamId === null ? ', active' : ''}`}
                  testID="drawer.team-all"
                >
                  <Text style={[styles.teamDropdownItemText, activeTeamId === null && { color: colors.primary, fontWeight: '600' as const }]} accessible={false}>All Teams</Text>
                  {activeTeamId === null && <Icon name="checkmark" size={ICON_SIZES.inline} color={colors.primary} />}
                </TouchableOpacity>
                {teams.map((team) => {
                  const isActive = team.id === activeTeamId;
                  const name = getTeamName(team);
                  return (
                    <TouchableOpacity
                      key={team.id}
                      style={[styles.teamDropdownItem, isActive && { backgroundColor: colors.primaryLight }]}
                      onPress={() => {
                        setActiveTeam(team.id);
                        setTeamDropdownOpen(false);
                      }}
                      accessibilityRole="button"
                      accessibilityState={{ selected: isActive }}
                      accessibilityLabel={`${name}${isActive ? ', active' : ''}`}
                      testID={`drawer.team-${team.id}`}
                    >
                      <Text style={[styles.teamDropdownItemText, isActive && { color: colors.primary, fontWeight: '600' as const }]} accessible={false}>{name}</Text>
                      {isActive && <Icon name="checkmark" size={ICON_SIZES.inline} color={colors.primary} />}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
          </View>
        )}

        <View style={styles.divider} />

        {/* Create Activity Shortcut */}
        <TouchableOpacity style={styles.createActivityButton} onPress={() => handleNavigate('/(tabs)/activities/create')} accessibilityRole="button" accessibilityLabel="Create new activity" testID="drawer.menu-new-activity">
          <Icon name="add" size={ICON_SIZES.menu} color={colors.primary} />
          <Text style={styles.createActivityText} accessible={false}>New Activity</Text>
        </TouchableOpacity>

        <View style={styles.divider} />

        {/* Navigation */}
        <Text style={styles.sectionLabel} accessibilityRole="header">Navigation</Text>
        <TouchableOpacity style={styles.menuItem} onPress={() => handleNavigate('/(tabs)/activities')} accessibilityRole="button" accessibilityLabel="Activities" testID="drawer.menu-activities">
          <Icon name="checkbox-outline" size={ICON_SIZES.menu} color={colors.primary} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={styles.menuText} accessible={false}>Activities</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.menuItem} onPress={() => handleNavigate('/(tabs)/goals')} accessibilityRole="button" accessibilityLabel="Goals" testID="drawer.menu-goals">
          <Icon name="flag-outline" size={ICON_SIZES.menu} color={colors.primary} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={styles.menuText} accessible={false}>Goals</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.menuItem} onPress={() => handleNavigate('/(tabs)/visions')} accessibilityRole="button" accessibilityLabel="Visions" testID="drawer.menu-visions">
          <Icon name="eye-outline" size={ICON_SIZES.menu} color={colors.primary} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={styles.menuText} accessible={false}>Visions</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.menuItem} onPress={() => handleNavigate('/(tabs)/time-score')} accessibilityRole="button" accessibilityLabel="Today's summary" testID="drawer.menu-time-score">
          <Icon name="stats-chart-outline" size={ICON_SIZES.menu} color={colors.primary} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={styles.menuText} accessible={false}>Today's Summary</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.menuItem} onPress={() => handleNavigate('/(tabs)/notifications')} accessibilityRole="button" accessibilityLabel="Notifications" testID="drawer.menu-notifications">
          <View style={{ position: 'relative', width: ICON_SIZES.menuWidth }}>
            <Icon name="notifications-outline" size={ICON_SIZES.menu} color={colors.primary} />
            {notificationUnreadCount > 0 && (
              <View style={{ position: 'absolute', top: -4, right: -4, backgroundColor: colors.error, borderRadius: 9, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 }}>
                <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>{notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}</Text>
              </View>
            )}
          </View>
          <Text style={styles.menuText} accessible={false}>Notifications</Text>
        </TouchableOpacity>

        <View style={styles.divider} />

        {/* Settings */}
        <TouchableOpacity style={styles.menuItem} onPress={() => handleNavigate('/(tabs)/settings')} accessibilityRole="button" accessibilityLabel="Settings" testID="drawer.menu-settings">
          <Icon name="settings-outline" size={ICON_SIZES.menu} color={colors.primary} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={styles.menuText} accessible={false}>Settings</Text>
        </TouchableOpacity>

        <View style={styles.divider} />

        {/* Logout */}
        <TouchableOpacity style={styles.menuItem} onPress={handleLogout} accessibilityRole="button" accessibilityLabel="Log out" testID="drawer.menu-logout">
          <Icon name="log-out-outline" size={ICON_SIZES.menu} color={colors.error} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={[styles.menuText, styles.logoutColor]} accessible={false}>Log Out</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.menuItem} onPress={handleLogoutForget} accessibilityRole="button" accessibilityLabel="Log out and forget device" accessibilityHint="Removes encryption key from this device" testID="drawer.menu-logout-forget">
          <Icon name="trash-outline" size={ICON_SIZES.menu} color={colors.errorText} style={{ width: ICON_SIZES.menuWidth }} />
          <Text style={[styles.menuText, styles.forgetColor]} accessible={false}>Log Out & Forget Device</Text>
        </TouchableOpacity>
        </ScrollView>
      </Animated.View>
    </Modal>
  );
}

const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.overlay,
  },
  drawer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    backgroundColor: theme.colors.surface,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 10,
  },
  header: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingHorizontal: 16,
    paddingTop: theme.headerPaddingTop,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '800' as const,
    color: theme.colors.textPrimary,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    borderRadius: 8,
  },
  profileSection: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.primaryLight,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: theme.colors.primary,
  },
  profileInfo: {
    flex: 1,
    marginLeft: 12,
  },
  profileName: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  profileEmail: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.bgTertiary,
    marginHorizontal: 16,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700' as const,
    color: theme.colors.textDisabled,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
  },
  menuItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  menuText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginLeft: 8,
  },
  teamDropdownContainer: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  teamDropdownTrigger: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    backgroundColor: theme.colors.bgSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  teamDropdownText: {
    fontSize: 14,
    fontWeight: '500' as const,
    color: theme.colors.textPrimary,
  },
  teamDropdownList: {
    backgroundColor: theme.colors.bgSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 4,
    overflow: 'hidden' as const,
  },
  teamDropdownItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  teamDropdownItemText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  createActivityButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    marginHorizontal: 16,
    marginVertical: 8,
    backgroundColor: theme.colors.primaryBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.primaryLight,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  createActivityText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: theme.colors.primary,
    marginLeft: 4,
  },
  logoutColor: {
    color: theme.colors.error,
  },
  forgetColor: {
    color: theme.colors.errorText,
  },
});
